import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useCart,
} from "../../components/CartContext";

import {
  fetchPriceSummary,
} from "../../actions/priceActions";

import {
  useDispatch,
} from "react-redux";

import "./css/CheckoutScreen.css";


/*
=========================================================
IMAGE URL HELPER
=========================================================
*/

function getImageUrl(image) {

  if (!image) {
    return "";
  }

  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  return `${image}`;
}


/*
=========================================================
CHECKOUT SCREEN
=========================================================
*/

function CheckoutScreen() {

  const {
    restaurantId,
    tableId,
  } = useParams();


  const navigate =
    useNavigate();


  const dispatch =
    useDispatch();


  /*
  =======================================================
  CART
  =======================================================
  */

  const {
    cart,
    cartCount,
    cartTotal,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
  } = useCart();


  /*
  =======================================================
  PRICE SUMMARY
  =======================================================
  */

  const [
    priceSummary,
    setPriceSummary,
  ] = useState(null);


  const [
    priceLoading,
    setPriceLoading,
  ] = useState(false);


  const [
    priceError,
    setPriceError,
  ] = useState(null);


  /*
  =======================================================
  FETCH PRICE SUMMARY FROM BACKEND
  =======================================================
  */

  useEffect(() => {

    if (
      !restaurantId ||
      cart.length === 0
    ) {
      return;
    }


    const loadPriceSummary =
      async () => {

        try {

          setPriceLoading(true);

          setPriceError(null);


          const items =
            cart.map(
              (item) => ({
                menu_item_id:
                  item.id,

                quantity:
                  Number(
                    item.quantity
                  ),
              })
            );


          const result =
            await dispatch(
              fetchPriceSummary(
                restaurantId,
                items
              )
            );


          if (result) {

            setPriceSummary(
              result
            );

          }

        } catch (error) {

          setPriceError(
            error?.message ||
            "Unable to calculate order total."
          );

        } finally {

          setPriceLoading(false);

        }

      };


    loadPriceSummary();

  }, [
    dispatch,
    restaurantId,
    cart,
  ]);


  /*
  =======================================================
  SERVER TOTALS
  =======================================================
  */

  const serverData =
    priceSummary?.data ||
    priceSummary ||
    {};


  const subtotal =
    Number(
      serverData.subtotal ??
      serverData.sub_total ??
      cartTotal ??
      0
    );


  const tax =
    Number(
      serverData.tax ??
      serverData.tax_amount ??
      0
    );


  const serviceCharge =
    Number(
      serverData.service_charge ??
      serverData.serviceCharge ??
      serverData.service_charge_amount ??
      0
    );


  const total =
    Number(
      serverData.total ??
      serverData.total_amount ??
      (
        subtotal +
        tax +
        serviceCharge
      )
    );


  /*
  =======================================================
  BACK TO CART
  =======================================================
  */

  const handleBackToCart = () => {

    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );

  };


  /*
  =======================================================
  CONTINUE TO ORDER
  =======================================================
  */

  const handleContinueToOrder = () => {

    if (
      cart.length === 0
    ) {
      return;
    }


    navigate(
      `/${restaurantId}/table/${tableId}/order`,
      {
        state: {
          subtotal,
          tax,
          serviceCharge,
          total,
        },
      }
    );

  };


  /*
  =======================================================
  EMPTY CART
  =======================================================
  */

  if (
    !cart ||
    cart.length === 0
  ) {

    return (

      <main className="theme5-checkout-page">

        <div className="theme5-checkout-empty">

          <span>
            CHECKOUT
          </span>

          <h1>
            YOUR CART
            <br />
            IS EMPTY.
          </h1>

          <p>
            Please add something delicious
            from our menu before continuing.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate(
                `/${restaurantId}/table/${tableId}`
              )
            }
          >
            VIEW MENU
            <span>→</span>
          </button>

        </div>

      </main>

    );

  }


  /*
  =======================================================
  RENDER
  =======================================================
  */

  return (

    <main className="theme5-checkout-page">


      {/* =================================================
          BLUE HEADER
      ================================================= */}

      <section className="theme5-checkout-hero">

        <div className="theme5-checkout-container">


          <button
            type="button"
            className="theme5-checkout-back"
            onClick={handleBackToCart}
          >

            <span>
              ←
            </span>

            BACK TO CART

          </button>


          <div className="theme5-checkout-heading">

            <span>
              YOUR SELECTION
            </span>

            <h1>
              CHECKOUT
            </h1>

            <p>

              {cartCount}

              {" "}

              {cartCount === 1
                ? "ITEM"
                : "ITEMS"
              }

              {" "}READY FOR THE TABLE.

            </p>

          </div>

        </div>

      </section>


      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <section className="theme5-checkout-content">

        <div className="theme5-checkout-container">


          <div className="theme5-checkout-layout">


            {/* =============================================
                LEFT SIDE
            ============================================= */}

            <div className="theme5-checkout-main">


              <div className="theme5-checkout-section-heading">

                <span>
                  01
                </span>

                <h2>
                  YOUR ORDER
                </h2>

              </div>


              <div className="theme5-checkout-items">


                {cart.map(
                  (
                    item,
                    index
                  ) => {

                    const imageUrl =
                      getImageUrl(
                        item.image ||
                        item.image_url ||
                        item.photo
                      );


                    const itemPrice =
                      Number(
                        item.price || 0
                      );


                    const itemTotal =
                      itemPrice *
                      Number(
                        item.quantity || 0
                      );


                    return (

                      <article
                        className="theme5-checkout-item"
                        key={
                          item.id ||
                          `${item.name}-${index}`
                        }
                      >


                        {/* IMAGE */}

                        <div className="theme5-checkout-item-image">

                          {imageUrl ? (

                            <img
                              src={imageUrl}
                              alt={item.name}
                            />

                          ) : (

                            <div className="theme5-checkout-no-image">

                              GREEK
                              <br />
                              KITCHEN

                            </div>

                          )}

                        </div>


                        {/* ITEM DETAILS */}

                        <div className="theme5-checkout-item-details">


                          <div className="theme5-checkout-item-number">

                            {String(
                              index + 1
                            ).padStart(
                              2,
                              "0"
                            )}

                          </div>


                          <h3>
                            {item.name}
                          </h3>


                          {item.description && (

                            <p>
                              {item.description}
                            </p>

                          )}


                          <div className="theme5-checkout-item-bottom">


                            {/* QUANTITY */}

                            <div className="theme5-checkout-quantity">

                              <button
                                type="button"
                                onClick={() =>
                                  decreaseQuantity(
                                    item.id
                                  )
                                }
                                aria-label={
                                  `Decrease ${item.name}`
                                }
                              >
                                −
                              </button>


                              <span>
                                {item.quantity}
                              </span>


                              <button
                                type="button"
                                onClick={() =>
                                  increaseQuantity(
                                    item.id
                                  )
                                }
                                aria-label={
                                  `Increase ${item.name}`
                                }
                              >
                                +
                              </button>

                            </div>


                            {/* REMOVE */}

                            <button
                              type="button"
                              className="theme5-checkout-remove"
                              onClick={() =>
                                removeFromCart(
                                  item.id
                                )
                              }
                            >
                              REMOVE
                            </button>


                            {/* PRICE */}

                            <strong>

                              ₹
                              {itemTotal.toFixed(
                                2
                              )}

                            </strong>

                          </div>

                        </div>

                      </article>

                    );

                  }
                )}

              </div>

            </div>


            {/* =============================================
                RIGHT SUMMARY
            ============================================= */}

            <aside className="theme5-checkout-summary">


              <span className="theme5-checkout-summary-label">

                TABLE {tableId}

              </span>


              <h2>

                YOUR
                <br />
                TOTAL

              </h2>


              <div className="theme5-checkout-summary-line" />


              <div className="theme5-checkout-summary-row">

                <span>
                  SUBTOTAL
                </span>

                <strong>

                  ₹
                  {subtotal.toFixed(
                    2
                  )}

                </strong>

              </div>


              <div className="theme5-checkout-summary-row">

                <span>
                  TAX
                </span>

                <strong>

                  ₹
                  {tax.toFixed(
                    2
                  )}

                </strong>

              </div>


              <div className="theme5-checkout-summary-row">

                <span>
                  SERVICE CHARGE
                </span>

                <strong>

                  ₹
                  {serviceCharge.toFixed(
                    2
                  )}

                </strong>

              </div>


              <div className="theme5-checkout-total">

                <span>
                  TOTAL
                </span>

                <strong>

                  ₹
                  {total.toFixed(
                    2
                  )}

                </strong>

              </div>


              {priceError && (

                <div className="theme5-checkout-error">

                  {priceError}

                </div>

              )}


              {priceLoading && (

                <div className="theme5-checkout-loading">

                  CALCULATING TOTAL...

                </div>

              )}


              <button
                type="button"
                className="theme5-checkout-continue"
                onClick={
                  handleContinueToOrder
                }
                disabled={
                  priceLoading
                }
              >

                {priceLoading
                  ? "CALCULATING..."
                  : "CONTINUE TO ORDER"
                }

                <span>
                  →
                </span>

              </button>


              <button
                type="button"
                className="theme5-checkout-add-more"
                onClick={() =>
                  navigate(
                    `/${restaurantId}/table/${tableId}`
                  )
                }
              >

                + ADD MORE TO CART

              </button>


              <p className="theme5-checkout-note">

                Payment method will be selected
                on the next step.

              </p>

            </aside>

          </div>


          {/* =================================================
              BOTTOM
          ================================================= */}

          <div className="theme5-checkout-bottom">

            <span>
              OPA!
            </span>

            <p>
              Good food. Good friends.
              Good times.
            </p>

          </div>

        </div>

      </section>

    </main>

  );

}


export default CheckoutScreen;